import INVENTORY_API_BASE_URL from "../../config/api";
import { useState, useEffect } from "react";
import Axios from "axios";
import Table from "react-bootstrap/Table";
import "./manageshelf.css";
import { CSVLink } from "react-csv";
import logUserActivity from "../utils/logUserActivity";

const getBarcodeCount = (row) =>
  Array.isArray(row?.barcodeData) ? row.barcodeData.length : 0;

function Manageshelf() {
  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [totalPresentQuantity, setTotalPresentQuantity] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [totalPresentQuantityWarehouse2, setTotalPresentQuantityWarehouse2] =
    useState(0);
  const [totalW1, setTotalW1] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    const loadShelves = async () => {
      try {
        const response = await Axios.get(
          `${INVENTORY_API_BASE_URL}/getManageshelf`
        );
        const shelves = Array.isArray(response.data)
          ? [...response.data].reverse()
          : [];
        const total = shelves.reduce(
          (sum, row) => sum + getBarcodeCount(row),
          0
        );
        const totalWarehouse2 = shelves.reduce(
          (sum, row) =>
            row.Warehouse === "Warehouse 2"
              ? sum + getBarcodeCount(row)
              : sum,
          0
        );

        if (isCurrent) {
          setData(shelves);
          setTotalPresentQuantity(total);
          setTotalPresentQuantityWarehouse2(totalWarehouse2);
          setTotalW1(total - totalWarehouse2);
          setLoadError("");
        }
      } catch (error) {
        console.error("Unable to load shelf inventory:", error);
        if (isCurrent) setLoadError("Shelf inventory could not be loaded.");
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadShelves();
    return () => {
      isCurrent = false;
    };
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete?");
    if (confirmed) {
      const deletedShelf = data.find((item) => item._id === id);
      await Axios.delete(`${INVENTORY_API_BASE_URL}/shelfdelete/${id}`);
      await logUserActivity("DELETE_SHELF", {
        shelfId: id,
        shelfDetails: {
          Warehouse: deletedShelf?.Warehouse,
          RackNumber: deletedShelf?.rackno,
          ShelfNumber: deletedShelf?.shelfno,
          SKU: deletedShelf?.sku,
          Title: deletedShelf?.title,
          Medium: deletedShelf?.Medium,
          Edition: deletedShelf?.edition,
          Capacity: deletedShelf?.capacity,
          PresentQuantity: deletedShelf?.presentquantity,
          EmptySpace: deletedShelf?.emptyspace,
          ShelfInventoryType: deletedShelf?.type,
          Timestamp: new Date().toISOString(),
        },
      });

      window.location.reload();
    }
  };

  const handleEdit = (row) => {
    setEditingId(row._id || `${row.rackno}-${row.shelfno}`);
    setEditedValues({ ...row });
  };

  const handleInputChange = (e) => {
    setEditedValues({ ...editedValues, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      await Axios.put(
        `${INVENTORY_API_BASE_URL}/editmanageshelf/${editedValues._id}`,
        editedValues
      );

      const original = data.find((row) => row._id === editedValues._id);
      const updatedFields = {};

      Object.keys(editedValues).forEach((key) => {
        if (editedValues[key] !== original[key]) {
          updatedFields[key] = {
            old: original[key],
            new: editedValues[key],
          };
        }
      });

      await logUserActivity("EDIT_SHELF", {
        shelfId: editedValues._id,
        updatedFields,
      });

      setEditingId(null);
      setEditedValues({});
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const csvHeaders = [
    { label: "Warehouse", key: "Warehouse" },
    { label: "Rack Number", key: "rackno" },
    { label: "Shelf Number", key: "shelfno" },
    { label: "SKU", key: "sku" },
    { label: "Title", key: "title" },
    { label: "Medium", key: "Medium" },
    { label: "Edition", key: "edition" },
    { label: "Capacity", key: "capacity" },
    { label: "Present Quantity", key: "presentquantity" },
    { label: "Empty Space", key: "emptyspace" },
    { label: "Shelf Inventory", key: "type" },
  ];

  const filteredData = searchTerm.trim()
    ? data.filter((row) => {
        const sku = row?.sku?.toString().toLowerCase() || "";
        const title = row?.title?.toString().toLowerCase() || "";
        const term = searchTerm.trim().toLowerCase();
        return sku.includes(term) || title.includes(term);
    })
    : data;
  const csvData = data.map((row) => ({
    ...row,
    presentquantity: getBarcodeCount(row),
  }));

  return (
    <main className="shelf-page">
      <section className="shelfcontainer" aria-label="Shelf inventory">
        <header className="shelf-toolbar">
          <div className="shelf-toolbar-heading">
            <p className="shelf-eyebrow"><span aria-hidden="true" /> INVENTORY / STOCK</p>
            <h1>Shelf inventory</h1>
            <p className="shelf-toolbar-description">Review stock levels and manage warehouse shelf locations.</p>
          </div>
          <div className="shelf-toolbar-actions">
            <span className="shelf-result-count" aria-live="polite">
              <strong>{filteredData.length.toLocaleString()}</strong>
              {filteredData.length === 1 ? " shelf" : " shelves"}
            </span>
            <label className="shelf-search-wrap">
              <span className="shelf-search-icon" aria-hidden="true" />
              <input
                className="shelf-search"
                type="search"
                placeholder="Search by SKU or title"
                aria-label="Search by SKU or title"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
            <CSVLink
              className="shelf-export"
              data={csvData}
              headers={csvHeaders}
              filename="shelf_data.csv"
            >
              <span aria-hidden="true">↓</span>
              Export CSV
            </CSVLink>
          </div>
        </header>

        <section className="shelf-summary" aria-label="Shelf inventory totals">
          <div className="shelf-metric shelf-metric--total">
            <span className="shelf-metric__icon" aria-hidden="true">Σ</span>
            <div>
              <span>Total books in stock</span>
              <strong>{totalPresentQuantity.toLocaleString()}</strong>
            </div>
          </div>
          <div className="shelf-metric shelf-metric--warehouse-one">
            <span className="shelf-metric__icon" aria-hidden="true">W1</span>
            <div>
              <span>Warehouse 1</span>
              <strong>{totalW1.toLocaleString()}</strong>
            </div>
          </div>
          <div className="shelf-metric shelf-metric--warehouse-two">
            <span className="shelf-metric__icon" aria-hidden="true">W2</span>
            <div>
              <span>Warehouse 2</span>
              <strong>{totalPresentQuantityWarehouse2.toLocaleString()}</strong>
            </div>
          </div>
        </section>

        {loadError && (
          <p role="alert" className="shelf-error">
            {loadError}
          </p>
        )}

        <div className="shelfcontent">
          {isLoading ? (
            <div className="shelf-state" role="status">
              <span className="shelf-state__spinner" aria-hidden="true" />
              Loading shelf inventory…
            </div>
          ) : loadError ? null : filteredData.length === 0 ? (
            <div className="shelf-state" role="status">
              {searchTerm
                ? "No shelves match this search."
                : "No shelf records are available."}
            </div>
          ) : (
            <Table className="manageshelftable" aria-label="Shelf inventory">
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th>Rack</th>
                  <th>Shelf</th>
                  <th>Item details</th>
                  <th>Capacity</th>
                  <th>Present qty</th>
                  <th>Empty space</th>
                  <th>Stock type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => {
                  const rowKey = row._id || `${row.rackno}-${row.shelfno}`;
                  const isEditing = editingId === rowKey;

                  return (
                    <tr key={rowKey}>
                      <td data-label="Warehouse">
                        <span className={row.Warehouse === "Warehouse 2" ? "shelf-warehouse-pill shelf-warehouse-pill--two" : "shelf-warehouse-pill"}>
                          {row.Warehouse}
                        </span>
                      </td>
                      <td data-label="Rack">
                        {isEditing ? (
                          <input
                            className="shelf-edit-input"
                            name="rackno"
                            value={editedValues.rackno || ""}
                            onChange={handleInputChange}
                          />
                        ) : (
                          row.rackno
                        )}
                      </td>
                      <td data-label="Shelf">
                        {isEditing ? (
                          <input
                            className="shelf-edit-input"
                            name="shelfno"
                            value={editedValues.shelfno || ""}
                            onChange={handleInputChange}
                          />
                        ) : (
                          row.shelfno
                        )}
                      </td>
                      <td data-label="Item details" className="shelf-item" title={row.title}>
                        <strong className="shelf-item__title">{row.title || "Untitled item"}</strong>
                        <span className="shelf-item__meta">
                          <span className="shelf-item__sku">{row.sku || "—"}</span>
                          {row.Medium && <span>{row.Medium}</span>}
                          {row.edition && <span>Edition {row.edition}</span>}
                        </span>
                      </td>
                      <td data-label="Capacity">{row.capacity}</td>
                      <td data-label="Present quantity">
                        <strong className="shelf-quantity">{getBarcodeCount(row)}</strong>
                      </td>
                      <td data-label="Empty space">{row.emptyspace}</td>
                      <td data-label="Stock type">
                        <span className={String(row.type || "").toLowerCase() === "good" ? "shelf-type-pill shelf-type-pill--good" : "shelf-type-pill shelf-type-pill--bad"}>
                          {row.type}
                        </span>
                      </td>
                      <td data-label="Actions">
                        {isEditing ? (
                          <div className="shelf-edit-actions">
                            <button
                              type="button"
                              className="shelf-button shelf-button-primary"
                              onClick={handleSave}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="shelf-button shelf-button-secondary"
                              onClick={() => {
                                setEditingId(null);
                                setEditedValues({});
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="shelf-actions">
                            <button
                              type="button"
                              className="shelf-button shelf-button-primary"
                              onClick={() => handleEdit(row)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="shelf-button shelf-button-danger"
                              onClick={() => handleDelete(row._id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </section>
    </main>
  );
}

export default Manageshelf;
